import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG_META = {
  weekly_paid_double_deducts_break: { label: 'Weekly "Paid" double-deducts break', cls: 'text-amber-300' },
  payroll_total_differs: { label: 'Payroll differs', cls: 'text-red-300' },
  weekly_total_differs: { label: 'Weekly total differs', cls: 'text-red-300' },
};
const prettyFlag = (f) => {
  if (FLAG_META[f]) return FLAG_META[f];
  const [k, n] = f.split(':');
  const map = {
    open_entries: 'Open entries', auto_closed: 'Auto-closed', fallback: 'Fallback',
    voided_excluded: 'Voided excluded', stored_duration_stale: 'Stored duration stale', timezone_day_shift: 'TZ day shift',
  };
  return { label: `${map[k] || k}${n ? ` (${n})` : ''}`, cls: 'text-[#C9C9C9]' };
};

const TimesheetReconcileDialog = ({ open, onOpenChange, weekStart, onOpenDay }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setData(null);
    axios.get(`${API}/admin/timesheet-reconciliation?week_start=${weekStart}`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, weekStart]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ts-reconcile-dialog" className="bg-[#141414] border-[#262626] text-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#D4AF37]" /> Payroll Reconciliation — {weekStart}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[10px] text-[#737373] uppercase tracking-wider">Read-only diagnostic · no records or exports were changed</p>

        {loading && <div className="flex items-center gap-2 text-[#C9C9C9] mt-6"><Loader2 className="w-4 h-4 animate-spin" /> Recomputing…</div>}
        {data?.error && <p className="text-red-300 mt-6">Could not load reconciliation.</p>}

        {data && !data.error && (
          <div className="space-y-4 mt-2">
            {data.reconciled ? (
              <div data-testid="ts-reconcile-ok" className="bg-green-500/10 border border-green-500/50 rounded-md p-3 text-green-200 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> All employees reconcile — recomputed paid hours match weekly totals and payroll.
              </div>
            ) : (
              <div data-testid="ts-reconcile-warning" className="bg-red-500/10 border border-red-500/50 rounded-md p-3 text-red-200 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {data.warning}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                  <tr className="text-[#D4AF37] text-[11px] uppercase tracking-wider">
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-right px-3 py-2">Recomputed Paid</th>
                    <th className="text-right px-3 py-2">Weekly Total</th>
                    <th className="text-right px-3 py-2">Weekly Paid</th>
                    <th className="text-right px-3 py-2">Payroll</th>
                    <th className="text-right px-3 py-2">Δ Payroll</th>
                    <th className="text-left px-3 py-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((e) => (
                    <tr key={e.user_id} data-testid={`ts-reconcile-row-${e.user_id}`} className={`border-b border-[#262626] ${e.reconciled ? '' : 'bg-red-500/[0.05]'}`}>
                      <td className="px-3 py-2 text-white font-medium">{e.user_name}</td>
                      <td className="px-3 py-2 text-right text-[#D4AF37] font-bold">{e.centralized_paid_hours.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-white">{e.weekly_total_hours.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-white">{e.weekly_paid_hours.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-white">{e.payroll_hours.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${Math.abs(e.diff_vs_payroll) > 0.01 ? 'text-red-300' : 'text-[#525252]'}`}>{e.diff_vs_payroll > 0 ? '+' : ''}{e.diff_vs_payroll.toFixed(2)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(e.flags || []).length === 0 ? <span className="text-[#525252]">—</span> : e.flags.map((f) => {
                            const m = prettyFlag(f);
                            return <span key={f} className={`text-[10px] font-bold ${m.cls}`}>{m.label}</span>;
                          })}
                        </div>
                        {/* Day links for drill-down */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(e.by_day || []).filter((d) => d.centralized_paid > 0 || Math.abs(d.difference) > 0.01).map((d) => (
                            <button
                              key={d.date}
                              data-testid={`ts-reconcile-day-${e.user_id}-${d.date}`}
                              onClick={() => onOpenDay?.(e.user_id, e.user_name, d.date)}
                              className={`text-[10px] px-1.5 py-0.5 rounded border ${Math.abs(d.difference) > 0.01 ? 'border-red-500/50 text-red-300 bg-red-500/10' : 'border-[#404040] text-[#C9C9C9] hover:bg-[#262626]'}`}
                              title="Open day detail"
                            >
                              {d.date.slice(5)}: {d.centralized_paid.toFixed(1)}h
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TimesheetReconcileDialog;
